import { Button, makeStyles, tokens } from '@fluentui/react-components'
import { t } from '../../i18n/i18n'
import { Page } from '../Page'
import { Title } from '../../components/common/texts/Title'
import { DataContext } from '../../App'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { OrganisationWithUserAccountsDTO } from '../../api/schemas/schema'
import { GridItem } from '../../components/common/grid/GridItem'
import { List } from '../../components/common/list/List'
import { deleteOrganisation, getOrganisationColumns, getOrganisations } from '../../helpers/organisationHelper'
import { Modal } from '../../components/common/modal/Modal'
import { UserMessage } from '../../components/common/messageBar/UserMessage'
import { ModalOrganisationForm } from './ModalOrganisationForm'
import { IModalState } from '../../interfaces/IModalState'
import { Loader } from '../../components/common/spinner/Loader'
const useStyles = makeStyles({
   flexColumn: {
      display: 'flex',
      flexDirection: 'column',
   },
   flexBtw: {
      display: 'flex',
      justifyContent: 'space-between',
   },
   flexCenter: {
      display: 'flex',
      alignItems: 'center',
   },
   flexEnd: {
      display: 'flex',
      justifyContent: 'flex-end',
   },
   columnIconSize: {
      fontSize: tokens.fontSizeBase300,
      marginLeft: tokens.spacingVerticalSNudge,
   },
   button: {
      marginBottom: tokens.spacingVerticalL,
   },
   cursor: {
      cursor: 'pointer',
   },
})

export const CustomerRegister = () => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { isLoading } = dataContext.state
   const [organisations, setOrganisations] = useState<OrganisationWithUserAccountsDTO[]>([])
   const [fetchError, setFetchError] = useState(false)
   const [selectedOrganisation, setSelectedOrganisation] = useState<OrganisationWithUserAccountsDTO>(null)
   const [modalState, setModalState] = useState<IModalState<OrganisationWithUserAccountsDTO>>({ isOpen: false, item: null })
   const existingOrganisationNumbers: string[] = useMemo(() => {
      const filteredOrganisations = organisations.filter((x) => x.organisation.id !== modalState.item?.organisation?.id)
      return filteredOrganisations.map((organisation) => organisation?.organisation?.organisationNumber)
   }, [modalState.item?.organisation?.id, organisations])

   const columns = useMemo(() => {
      return getOrganisationColumns(setSelectedOrganisation)
   }, [])

   const fetch = () => {
      getOrganisations(setOrganisations, dataContext, setFetchError)
   }

   useEffect(() => {
      const controller = new AbortController()
      getOrganisations(setOrganisations, dataContext, setFetchError, controller.signal)
      return () => controller.abort()
      //eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])
   const onModalClose = () => {
      setModalState({ isOpen: false, item: null })
      fetch()
   }
   return (
      <>
         <Page header={<Title as="h2">{t('CustomerRegister')}</Title>} subheader={<div className={classes.flexColumn}></div>} stickySubHeader>
            {
               <>
                  {dataContext.state.isLoading ? (
                     <Loader />
                  ) : (
                     <GridItem size="12/12">
                        <div className={classes.flexEnd}>
                           <Button
                              appearance="primary"
                              className={organisations.length === 0 && classes.button}
                              onClick={() => setModalState({ isOpen: true, item: null })}
                           >
                              {t('NewCustomer')}
                           </Button>
                        </div>
                        {fetchError ? (
                           <UserMessage type="error" title="FetchFailed" message="CouldNotFetchOrganisations" />
                        ) : (
                           <>
                              {organisations.length > 0 && (
                                 <List
                                    sortable
                                    columns={columns}
                                    items={organisations}
                                    loading={isLoading}
                                    onRowClick={(item) => setModalState({ isOpen: true, item })}
                                    asCard
                                 />
                              )}
                              {organisations.length === 0 && <UserMessage title="NoOrganisationsFound" message="AddFirstCustomerMsg" />}
                           </>
                        )}
                        <Modal
                           open={selectedOrganisation !== null}
                           title={t('DeleteOrganisation')}
                           children={
                              <div>
                                 <p>{`${t('DeleteConfirmationMsg')} ${selectedOrganisation?.organisation?.name}?`}</p>
                                 <p>{t('AreYouSureDeleteOrganisation')}</p>
                              </div>
                           }
                           actions={
                              <>
                                 <Button appearance="subtle" onClick={() => setSelectedOrganisation(null)}>
                                    {t('Cancel')}
                                 </Button>
                                 <Button
                                    appearance="primary"
                                    onClick={() => deleteOrganisation(selectedOrganisation.organisation.id, dataContext, setSelectedOrganisation, fetch)}
                                 >
                                    {t('Delete')}
                                 </Button>
                              </>
                           }
                        />
                     </GridItem>
                  )}
               </>
            }
         </Page>

         <ModalOrganisationForm
            existingOrganisationNumbers={existingOrganisationNumbers}
            modalState={modalState}
            setModalState={setModalState}
            onModalClose={onModalClose}
         />
      </>
   )
}
